import { useLazyQuery } from "@apollo/client/react";
import { formatInTimeZone } from "date-fns-tz";
import { useState } from "react";

import { TField, TGroup, TSection } from "@smart/bridge-resources-basic";
import {
  createFilterClientSolicitor,
  createFilterHiddenFields,
  defaultClientRoleId,
  groupResponse,
  lookupMatchingContacts,
  MatchingContact,
  SBContact,
  SBContactType,
  isCreateAndReplaceCurrentContact,
  isUsingExistingContact,
  getSentToIndividualClientIndex,
  groupLayoutItemsByClientRole,
  getClientRoleId,
  groupLayoutItemsByCrossRoleRepeatable,
  formatSSN,
} from "@smart/bridge-smokeball-basic";
import {
  MatterLayoutInfoCrossRoleRepeatableKey,
  MatterLayoutInfoParentIdKey,
  ResponseSyncStatus,
  SubmissionSyncStatus,
} from "@smart/bridge-types-basic";
import { loadDefaultCountry } from "@smart/itops-locale-dom";
import { Matter, Roles, useSmokeballApp } from "@smart/itops-smokeball-app-dom";
import {
  backoff,
  delay,
  entriesOf,
  fromEntries,
  jsonParse,
  logError,
  resolveSettled,
  timeout,
} from "@smart/itops-utils-basic";
import {
  useUpdateSubmission,
  useSyncSubmission,
  MatterSubmission,
  useLazyLoadContacts,
  useUpdateResponseSyncStatus,
  useQueryMatterLayoutFields,
  useUpdateContact,
} from "@smart/manage-gql-client-dom";
import { Gql, queryDocuments } from "@smart/manage-gql-operations-dom";

import { useUser } from "./user";

export type SyncOptions = {
  submission: MatterSubmission;
  fields: Gql.FieldFieldsFragment[];
  groups: Gql.GroupFieldsFragment[];
  sections: Gql.SectionFieldsFragment[];
  formMatterTypeRepresentatives?: string[];
  shouldReviewConflict?: boolean;
};

type SyncResult = "completed" | "backendSynced" | "pendingContactReview";

type ContactInput = Record<SBContactType, Record<string, any>>;

type ProcessResponsesOptions = SyncOptions & {
  matter: Matter | undefined;
  clientRoleId: string | undefined;
  sentToIndividualClientIndex: number;
  filterResponses?: (response: {
    field: TField;
    group?: TGroup;
    value: any;
    updatedAt: number;
    uri: string;
  }) => boolean;
  getLayoutFields: ({
    layoutId,
    providerId,
  }: {
    layoutId: string;
    providerId: string;
  }) => Promise<NonNullable<TField["field"]>[]>;
};

export const processResponses = ({
  matter,
  groups,
  fields,
  sections,
  submission,
  clientRoleId,
  sentToIndividualClientIndex,
  formMatterTypeRepresentatives,
  filterResponses = () => true,
  getLayoutFields,
}: ProcessResponsesOptions) => {
  const notToSyncStatus = ["synced", "notToSync"] as ResponseSyncStatus[];
  const responses = fromEntries(
    submission.responses
      .filter((r) => !r.syncStatus || !notToSyncStatus.includes(r.syncStatus))
      .map((r) => [
        r.fieldUri,
        {
          uri: r.uri,
          value: jsonParse(r.value, "response value"),
          existingItemIds: r.existingItemIds,
          reviewBeforeUpdate: r.reviewBeforeUpdate,
          syncStatus: r.syncStatus,
        },
      ]),
  );
  const backendResponseUpdatedTime =
    submission.responses.reduce<Record<string, number>>(
      (aggr, r) => ({
        ...aggr,
        [r.fieldUri]: new Date(r.updatedAt).getTime(),
      }),
      {},
    ) || {};

  const groupsMap = fromEntries(groups.map((g) => [g.uri, g.values]));
  const fieldsMap = fromEntries(fields.map((f) => [f.uri, f.values]));

  const filterHiddenFields = createFilterHiddenFields({
    sections,
    groups,
    fields: fields.map((f) => f.values),
    responses: fromEntries(
      submission.responses.map((r) => [
        r.fieldUri,
        jsonParse(r.value, "parse response"),
      ]),
    ),
  });
  const fieldResponses = fields
    .map((field) => ({
      field: field.values as TField,
      group: fieldsMap[field.uri].groupUri
        ? (groupsMap[fieldsMap[field.uri].groupUri!] as TGroup)
        : undefined,
      value: responses[field.uri]?.value,
      updatedAt: backendResponseUpdatedTime[field.uri] || 0,
      uri: responses[field.uri]?.uri,
      existingItemIds: responses[field.uri]?.existingItemIds || undefined,
      reviewBeforeUpdate: responses[field.uri]?.reviewBeforeUpdate || undefined,
      syncStatus: responses[field.uri]?.syncStatus || undefined,
    }))
    /**
     * undefined response value indicates that the field hasn't been anwsered before
     * while null indicates that the answer is removed.
     */
    .filter((r) => r.value !== undefined && !!r.uri)
    .filter(filterHiddenFields)
    .filter(filterResponses);
  const isFormCategoryDifferent =
    !!matter &&
    matter.isLead !==
      (submission.rawSubmission.form.values.category === "lead");

  return groupResponse({
    clientRoleId,
    sentToIndividualClientIndex,
    sections: sections.map((s) => s.values as TSection),
    fieldResponses,
    country: loadDefaultCountry(),
    isFormCategoryDifferent,
    formMatterTypeRepresentatives,
    getLayoutFields,
    fields: fields.map((f) => f.values as TField),
  });
};

export const useSyncToMatter = () => {
  const { user } = useUser();
  const { matters, layouts, roles, contacts, relationships } =
    useSmokeballApp();

  const searchContacts = useLazyLoadContacts();
  const [updateContact] = useUpdateContact();
  const [loadSubmission] = useLazyQuery(queryDocuments.submission, {
    fetchPolicy: "network-only",
  });
  const [backendSync] = useSyncSubmission();
  const [updateSubmission] = useUpdateSubmission({ user });
  const updateSyncStatus = (
    { uri, formUri }: { uri: string; formUri: string },
    syncStatus: SubmissionSyncStatus,
  ) =>
    updateSubmission({
      variables: {
        uri,
        formUri,
        fields: {
          syncStatus,
        },
      },
    });
  const [updateResponseSyncStatus] = useUpdateResponseSyncStatus();
  const loadMatterLayoutFields = useQueryMatterLayoutFields();
  const [isSyncing, setIsSyncing] = useState(false);

  const setResponsesSyncStatus = async (
    uriSet: Set<string> | undefined,
    action: string,
    syncStatus: "synced" | "notToSync",
    info?: string,
  ) => {
    if (!uriSet) return;

    try {
      await Promise.allSettled(
        Array.from(uriSet).map((uri) =>
          updateResponseSyncStatus({
            variables: {
              uri,
              syncStatus,
              info,
            },
          }),
        ),
      );
    } catch (error) {
      console.error(`Error set responses as "synced" for ${action}.`, error);
    }
  };

  const waitForStatus = async (uri: string) => {
    let complete = false;
    let syncStatus: Gql.SubmissionFieldsFragment["values"]["syncStatus"];

    while (!complete) {
      await delay(500);
      const result = await loadSubmission({ variables: { uri } });
      syncStatus = result.data?.submission?.values.syncStatus;

      if (syncStatus === "synced" || syncStatus === "failedToSync")
        complete = true;
    }

    if (syncStatus === "failedToSync") throw new Error("API failed to sync");

    return syncStatus;
  };

  const convertDateFields = (
    data: Record<string, any>,
    dateFields: string[],
  ) => {
    if (!data) return data;

    const dateInputRegex = /^\d{4}-\d{2}-\d{2}$/;
    const converted = { ...data };

    dateFields.forEach((field) => {
      if (data[field]) {
        const value = dateInputRegex.test(data[field])
          ? // Use UTC time to avoid timezone issues
            new Date(`${data[field]}T00:00:00Z`)
          : data[field];
        converted[field] = value;
      }
    });

    return converted;
  };

  const convertContactInput = (input: ContactInput): ContactInput => {
    /**
     * SDK requires "Date" values to be of Date data type.
     * Reference: https://docs.smokeball.com/docs/sdk-docs/api/@smokeballdev/namespaces/contacts/interfaces/Person#interface%3A-person
     */
    const converted = { ...input };
    if (converted.person) {
      converted.person = convertDateFields(input.person, ["birthDate"]);
      if (converted.person.birthDate) {
        // It's person.birthdate (all lower-case) in the SDK not person.birthDate as in the API.
        converted.person.birthdate = converted.person.birthDate;
        delete converted.person.birthDate;
      }
      converted.person.passportDetails = convertDateFields(
        input.person.passportDetails,
        ["passportIssueDate", "passportExpireDate"],
      );
      converted.person.deathDetails = convertDateFields(
        input.person.deathDetails,
        ["deathDate"],
      );
      if (
        converted.person.identificationNumberType?.toUpperCase() !== "ITIN" &&
        converted.person.identificationNumber
      ) {
        converted.person.identificationNumber = formatSSN(
          converted.person.identificationNumber,
        );
      }
    }

    if (converted.trust) {
      converted.trust = convertDateFields(input.trust, ["executedDate"]);
    }

    return converted;
  };

  const convertToBackendFormat = (
    input: ContactInput,
  ): Record<string, unknown> => {
    const contactInput: Record<string, unknown> = {};
    if (input.person) {
      const person = { ...input.person };
      const birthValue = (person.birthdate ?? person.birthDate) as
        | Date
        | string
        | undefined;
      if (birthValue != null) {
        if (typeof birthValue === "string") {
          person.birthDate = birthValue;
        } else if (birthValue instanceof Date) {
          person.birthDate = formatInTimeZone(birthValue, "UTC", "yyyy-MM-dd");
        }
      }
      delete person.birthdate;
      ["passportDetails", "deathDetails"].forEach((key) => {
        const details = person[key as keyof typeof person] as
          | Record<string, unknown>
          | undefined;
        if (details && typeof details === "object") {
          const out = { ...details };
          Object.keys(out).forEach((k) => {
            const v = (out as Record<string, unknown>)[k];
            if (v instanceof Date)
              (out as Record<string, unknown>)[k] = formatInTimeZone(
                v as Date,
                "UTC",
                "yyyy-MM-dd",
              );
          });
          person[key as keyof typeof person] = out;
        }
      });
      contactInput.person = person;
    }
    if (input.company) contactInput.company = input.company;
    if (input.trust) {
      const trust = { ...input.trust };
      const executed = trust.executedDate;
      if (executed instanceof Date) {
        trust.executedDate = formatInTimeZone(executed, "UTC", "yyyy-MM-dd");
      }
      contactInput.trust = trust;
    }

    return contactInput;
  };

  return {
    sync: async ({
      groups,
      fields,
      sections,
      submission,
      formMatterTypeRepresentatives,
      shouldReviewConflict,
    }: SyncOptions): Promise<SyncResult | undefined> => {
      try {
        setIsSyncing(true);
        await updateSyncStatus(submission, "syncing");

        const partialMatchingContacts: MatchingContact[] = [];

        const existingRoles = await roles?.get();
        const existingClientRoleName = getClientRoleId(
          existingRoles?.roles || [],
        );

        const grouped = await processResponses({
          matter: matters?.current,
          groups,
          fields,
          sections,
          submission,
          formMatterTypeRepresentatives,
          clientRoleId: existingClientRoleName,
          sentToIndividualClientIndex: getSentToIndividualClientIndex(
            existingRoles?.roles || [],
            submission.recipient?.contactId,
          ),
          filterResponses: createFilterClientSolicitor(existingClientRoleName),
          getLayoutFields: async ({ layoutId, providerId }) => {
            const matterFields = await loadMatterLayoutFields({
              matterLayoutId: layoutId,
              providerId,
            });

            return matterFields.map((f) => ({
              ...f,
              possibleValues: f.possibleValues || undefined,
              dataSourceMapping: f.dataSourceMapping || undefined,
            }));
          },
        });

        const performBackendSync = async () => {
          await backendSync({ variables: { uri: submission.uri } });
          await Promise.race([
            waitForStatus(submission.uri),
            timeout(10 * 60000, "API syncing timed out"),
          ]);
        };

        /**
         * The SDK has yet to support Contact Layout APIs.
         * So if there is any LayoutContact responses, use BackendSync instead.
         */
        const hasLayoutContacts =
          Object.values(grouped.layoutContacts).length > 0;
        const hasReviewedCompanyContactsWithRelation = [
          ...Object.values(grouped.roles),
          ...Object.values(grouped.relationships).flatMap(
            (roleContactResponses) => Object.values(roleContactResponses),
          ),
        ].some(
          (contactResponse) =>
            contactResponse.hasRelation && contactResponse.isReviewed,
        );
        if (hasLayoutContacts || hasReviewedCompanyContactsWithRelation) {
          console.warn(
            "Sync layout contacts or company contact with relation will be handled by backend",
          );
          await performBackendSync();
          return "backendSynced";
        }

        const doCreateOrUpdateContact = async (
          existingRoleContactId: string | undefined,
          contactInput: ContactInput,
        ): Promise<SBContact | undefined> => {
          const existingContact = existingRoleContactId
            ? await contacts?.get(existingRoleContactId)
            : undefined;

          // This can be extended to other fields that not supported by the SDK.
          const shouldUseBackend = (
            existing: SBContact | undefined,
            input: ContactInput,
          ): boolean =>
            existing?.person?.identificationNumber !==
              input.person?.identificationNumber ||
            existing?.person?.identificationNumberType !==
              input.person?.identificationNumberType;

          if (shouldUseBackend(existingContact, contactInput)) {
            try {
              const result = await updateContact({
                variables: {
                  existingContactId: existingContact?.id,
                  contactInput: convertToBackendFormat(contactInput),
                },
              });

              if (!result.data?.updateContact)
                throw new Error("No contact updated.");

              return result.data.updateContact as SBContact;
            } catch (error) {
              console.error(
                "Attempted to update contact using API, but failed. ",
                error,
              );
            }
          }

          return existingRoleContactId
            ? contacts?.update({
                id: existingRoleContactId,
                ...contactInput,
              })
            : contacts?.create(contactInput);
        };

        const updateOrCreateContact = async ({
          existingContactId,
          existingRoleContactId,
          contactInput,
        }: {
          existingContactId: string | undefined;
          existingRoleContactId: string | undefined;
          contactInput: ContactInput;
        }) => {
          if (isUsingExistingContact(existingContactId))
            return { id: existingContactId };

          if (
            existingRoleContactId &&
            !isCreateAndReplaceCurrentContact(existingContactId)
          ) {
            const existingContact = await contacts?.get(existingRoleContactId);

            return doCreateOrUpdateContact(
              (existingContact?.groupOfPeople?.people?.[0] as string) ||
                existingRoleContactId,
              contactInput,
            );
          }

          const matchingContacts = !isCreateAndReplaceCurrentContact(
            existingContactId,
          )
            ? await lookupMatchingContacts({
                input: contactInput,
                search: async ({ names, emails, phones }) => {
                  const searchResult = await searchContacts({
                    names,
                    emails,
                    phones,
                  });

                  return (searchResult as SBContact[]) || [];
                },
              })
            : [];

          const exactMatch = matchingContacts.find(
            (mc) => mc.matchType === "exact",
          );
          if (exactMatch) return exactMatch;

          partialMatchingContacts.push(
            ...matchingContacts.filter((mc) => mc.matchType === "partial"),
          );
          return shouldReviewConflict &&
            partialMatchingContacts.length &&
            !isCreateAndReplaceCurrentContact(existingContactId)
            ? undefined
            : doCreateOrUpdateContact(undefined, contactInput);
        };

        const updateRoles = entriesOf(grouped.roles).map(
          async ([responseRoleName, items]) => {
            const roleName =
              responseRoleName === defaultClientRoleId
                ? existingClientRoleName
                : responseRoleName;

            const foundRoles = existingRoles?.roles?.filter(
              (r) => r.name === roleName,
            );
            const recipientContactId = submission.recipient?.contactId;
            const isClientRole = roleName === existingClientRoleName;
            const isHandlingMultiClients =
              isClientRole &&
              !items.group?.length &&
              recipientContactId &&
              foundRoles &&
              foundRoles.length > 1;
            const contactItems = items.group?.length ? items.group : [items];

            const result = await Promise.all(
              contactItems.map(async (contactItem, index) => {
                const found =
                  foundRoles && isHandlingMultiClients
                    ? foundRoles.find((r) => r.contactId === recipientContactId)
                    : foundRoles?.[index];
                const isExistingRoleContactGroupOfPeople = found?.contactId
                  ? !!(await contacts?.get(found.contactId))?.groupOfPeople
                  : false;
                const contact = await updateOrCreateContact({
                  existingContactId: items.existingContactIds?.[index],
                  existingRoleContactId: found?.contactId,
                  contactInput: convertContactInput(contactItem),
                });
                if (contact) {
                  if (!found && roleName) {
                    await roles?.add({
                      name: roleName,
                      contactId: contact.id,
                    });
                  } else if (
                    found &&
                    (!found.contactId ||
                      (found.contactId !== contact.id &&
                        !isExistingRoleContactGroupOfPeople))
                  ) {
                    await roles?.update({
                      id: found.id,
                      contactId: contact.id,
                    });
                  }
                }

                return contact;
              }),
            );
            if (result.every(Boolean)) {
              await setResponsesSyncStatus(
                grouped.responseUris.roles[responseRoleName],
                `syncing for role=${responseRoleName}`,
                items.existingContactIds?.length &&
                  items.existingContactIds.every(isUsingExistingContact)
                  ? "notToSync"
                  : "synced",
                items.existingContactIds?.filter(Boolean).length
                  ? "The role has multiple contacts"
                  : undefined,
              );
            }
          },
        );

        const updateRelationships = (allExistingRoles: Roles | undefined) =>
          entriesOf(grouped.relationships).map(
            async ([roleName, relationshipItems]) => {
              const existingRole = allExistingRoles?.roles.find(
                (r) => r.name === roleName,
              );
              /**
               * The current SDK doesn't support creating a role without contact.
               * So we just process existing roles at the moment.
               */
              if (!existingRole) return;

              await Promise.all(
                entriesOf(relationshipItems).map(
                  async ([relationshipId, items]) => {
                    const contactItems = items.group?.length
                      ? items.group
                      : [items];

                    await Promise.all(
                      contactItems.map(async (contactItem, index) => {
                        const existingRelationships = await relationships?.get(
                          existingRole.id,
                        );
                        const matchedExistingRelationships =
                          existingRelationships?.relationships.filter(
                            (r) => r.name === relationshipId,
                          );
                        const contact = await updateOrCreateContact({
                          existingContactId: items.existingContactIds?.[index],
                          existingRoleContactId:
                            matchedExistingRelationships?.[index]?.contactId,
                          contactInput: convertContactInput(contactItem),
                        });
                        if (contact) {
                          await (matchedExistingRelationships?.[index]
                            ? relationships?.update({
                                id: matchedExistingRelationships?.[index].id,
                                roleId: roleName,
                                contactId: contact.id,
                              })
                            : relationships?.add({
                                roleId: roleName,
                                name: relationshipId,
                                contactId: contact.id,
                              }));

                          await setResponsesSyncStatus(
                            grouped.responseUris.relationships[
                              `${roleName}/${relationshipId}`
                            ],
                            `syncing for relationship=${relationshipId}`,
                            items.existingContactIds?.length &&
                              items.existingContactIds.every(
                                isUsingExistingContact,
                              )
                              ? "notToSync"
                              : "synced",
                            items.existingContactIds?.filter(Boolean).length
                              ? "The relationship has multiple contacts"
                              : undefined,
                          );
                        }
                      }),
                    );
                  },
                ),
              );
            },
          );
        const settledUpdateRolePromises = await Promise.allSettled(updateRoles);

        const updateLayouts = entriesOf(grouped.layouts).map(
          async ([layoutId, items]) => {
            const layoutParentId = items[MatterLayoutInfoParentIdKey];
            delete items[MatterLayoutInfoParentIdKey];
            const crossRoleRepeatableItems =
              items[MatterLayoutInfoCrossRoleRepeatableKey];
            delete items[MatterLayoutInfoCrossRoleRepeatableKey];

            const layoutItemsWithSameDesignId = await backoff({
              run: async () =>
                (await layouts?.get(matters?.current?.id))?.value?.filter(
                  (l) => l.layoutDesign.id === layoutId,
                ),
              test: (found) =>
                crossRoleRepeatableItems?.length
                  ? !!found?.length &&
                    found.length >= crossRoleRepeatableItems.length
                  : true,
              base: 1000,
              attempts: 10,
              shouldCatch: false,
            })();

            if (!layoutItemsWithSameDesignId?.length) {
              console.warn(
                `Layout items for layout design id ${layoutId} not found.`,
              );
            }

            const layoutItemsGroupedByClientRole = groupLayoutItemsByClientRole(
              {
                items,
                shouldGroup:
                  existingClientRoleName &&
                  layoutParentId &&
                  layoutParentId === existingClientRoleName,
              },
            );

            const groupedLayoutItems = groupLayoutItemsByCrossRoleRepeatable({
              groupedLayoutItems: layoutItemsGroupedByClientRole,
              crossRoleRepeatableItems,
            });

            for (const [
              index,
              layoutItemsToUpdate,
            ] of groupedLayoutItems.entries()) {
              const itemId = layoutItemsWithSameDesignId?.[index]?.itemId;
              if (!itemId) {
                console.warn(
                  `No layout item id for layout ${layoutId} index ${index}; skipping update.`,
                );
                // eslint-disable-next-line no-continue
                continue;
              }
              const values = entriesOf(layoutItemsToUpdate).map(
                ([key, value]) => ({
                  key,
                  value,
                }),
              );
              if (values.length) {
                await layouts?.update({
                  itemId,
                  layoutId,
                  values,
                });
              }
            }

            await setResponsesSyncStatus(
              grouped.responseUris.layouts[layoutId],
              `syncing for layout=${layoutId}`,
              "synced",
            );
          },
        );
        /**
         * Layout updates have to start after role updates so that the role updates create layout items.
         * For example, a repeatable group with a role field needs to create layout item entry so that the
         * sub layout items can be updated.
         */
        const settledUpdateLayoutPromises =
          await Promise.allSettled(updateLayouts);

        const { rejected } = await resolveSettled([
          ...settledUpdateRolePromises,
          ...settledUpdateLayoutPromises,
        ]);

        /**
         * Updating relationships have to start after updating roles,
         * so that it can have latest roles.
         */
        const latestExistingRoles = await roles?.get();
        const { rejected: rejectedRelationships } = await resolveSettled(
          Promise.allSettled(updateRelationships(latestExistingRoles)),
        );

        if (rejected.length === 0 && rejectedRelationships.length === 0) {
          if (shouldReviewConflict && partialMatchingContacts.length > 0) {
            await updateSyncStatus(submission, "pendingContactReview");
            return "pendingContactReview";
          }

          await updateSyncStatus(submission, "synced");
          return "completed";
        }

        console.warn("Failed to sync via SDK, falling back to API");
        [...rejected, ...rejectedRelationships].forEach((error) =>
          logError(error, "sdk syncing"),
        );

        await performBackendSync();
        return "backendSynced";
      } catch (error) {
        await updateSyncStatus(submission, "failedToSync");
        logError(error, "syncing");
        throw error;
      } finally {
        setIsSyncing(false);
      }
    },
    isSyncing,
  };
};
